import { useState, useEffect, useMemo, useRef } from "react";
import { formatNZDate, getNZNow } from "../../utils/timezone";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Star,
  AlertCircle,
  Calendar,
  ExternalLink,
  Timer,
  TrendingDown,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AIRecommendationDialog } from "./AIRecommendationDialog";

// ---- Sub: live "X days remaining" countdown ----
function ExpiryCountdown({ expiryTime }) {
  const [timeLeft, setTimeLeft] = useState("");
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expiryTime) return;
    const update = () => {
      const now = getNZNow();
      const expiry = new Date(expiryTime);
      if (typeof expiryTime === "string" && /^\d{4}-\d{2}-\d{2}$/.test(expiryTime)) {
        expiry.setHours(23, 59, 59, 999);
      }
      const diff = expiry - now;
      if (diff <= 0) {
        setIsExpired(true);
        setTimeLeft("Expired");
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      if (days > 0) setTimeLeft(`Valid for ${days}d ${hours}h`);
      else setTimeLeft(`Valid for ${hours}h`);
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [expiryTime]);

  if (!expiryTime) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] ${
        isExpired ? "text-red-500" : "text-muted-foreground"
      }`}
      data-testid="expiry-countdown"
    >
      <Timer className="h-3 w-3" />
      {timeLeft}
    </span>
  );
}

function isQuoteExpired(quote, request) {
  // A quote is "expired" only if its OWN quote_expiry has passed. The
  // parent request's `expiry_time` is the cutoff for *new* shop quotes
  // — it does NOT invalidate quotes already on the table. Each shop's
  // quote stays honourable until that quote's own quote_expiry.
  //
  // Older quotes (pre-multi-quote era) may not have their own
  // quote_expiry — for those, fall back to the request expiry.
  if (quote.quote_expiry) {
    const e = new Date(quote.quote_expiry);
    e.setHours(23, 59, 59, 999);
    return new Date() > e;
  }
  if (request?.expiry_time) {
    const e = new Date(request.expiry_time);
    e.setHours(23, 59, 59, 999);
    return new Date() > e;
  }
  return false;
}

// ---- Math helpers (mirrors backend's GST treatment) ----
function computePricing(quote, request) {
  const pb = quote.price_breakdown || {};
  const inclFactor = pb.prices_include_gst ? 1 : 1.15;
  const tyreFitmentRaw = pb.tyre_fitment || pb.tyres || 0;
  const tyreFitmentInclGst = tyreFitmentRaw * inclFactor;
  const alignmentInclGst = (pb.alignment || 0) * inclFactor;
  const rotationInclGst = (pb.rotation || 0) * inclFactor;
  const totalInclGst = quote.total_price || quote.price || 0;
  const qty = request?.quantity || 4;
  const isTyreQuote =
    (request?.service_type?.includes("tyres") ||
      request?.selected_services?.includes("tyres")) &&
    tyreFitmentInclGst > 0 &&
    qty > 0;
  const perTyreInclGst = isTyreQuote ? tyreFitmentInclGst / qty : 0;
  const perTyreExclGst = perTyreInclGst / 1.15;
  return {
    isTyreQuote,
    qty,
    tyreFitmentInclGst,
    alignmentInclGst,
    rotationInclGst,
    totalInclGst,
    perTyreInclGst,
    perTyreExclGst,
  };
}

// ---- Sub: a single quote card column ----
function QuoteColumn({ quote, request, isCheapest, onAccept }) {
  const p = computePricing(quote, request);
  const expired = isQuoteExpired(quote, request);
  const accepted = quote.status === "accepted";
  const rejected = quote.status === "rejected";
  const interactive = !expired && !accepted && !rejected;
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [notesOverflows, setNotesOverflows] = useState(false);
  const notesRef = useRef(null);
  const notes = quote.notes || "";

  // Detect real overflow (scrollHeight > clientHeight when clamped).
  // Re-checks on quote / window resize so the Read more link stays
  // accurate when the card width changes.
  useEffect(() => {
    if (!notes) return;
    const check = () => {
      const el = notesRef.current;
      if (!el) return;
      setNotesOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [notes]);

  const stateClasses = accepted
    ? "border-green-500/60 bg-green-500/5"
    : expired || rejected
    ? "border-input opacity-60"
    : isCheapest
    ? "border-primary shadow-[0_0_0_2px_rgba(249,115,22,0.15)]"
    : "border-input";

  return (
    <div
      className={`relative shrink-0 w-[290px] rounded-xl border-2 bg-card transition-colors ${stateClasses}`}
      data-testid={`quote-card-${quote.quote_id}`}
    >
      {/* BEST PRICE ribbon */}
      {isCheapest && interactive && !accepted && (
        <div className="absolute -top-2 left-3 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 shadow-sm">
          <TrendingDown className="h-3 w-3" /> BEST PRICE
        </div>
      )}
      {accepted && (
        <div className="absolute -top-2 left-3 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">
          ACCEPTED
        </div>
      )}
      {expired && (
        <div className="absolute -top-2 left-3 bg-gray-500 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">
          EXPIRED
        </div>
      )}

      <div className="p-4 pt-5 space-y-3">
        {/* Shop strip */}
        <div>
          <p className="font-semibold text-sm truncate" data-testid={`shop-name-${quote.quote_id}`}>
            {quote.shop_name || "Tyre Shop"}
          </p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
            {quote.google_rating != null && (
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                <span className="font-medium text-foreground">
                  {quote.google_rating.toFixed(1)}
                </span>
                {quote.google_review_count != null && (
                  <span>({quote.google_review_count})</span>
                )}
              </span>
            )}
            {quote.distance_km != null && (
              <>
                {quote.google_rating != null && <span>•</span>}
                <span>{quote.distance_km.toFixed(1)} km away</span>
              </>
            )}
          </div>
        </div>

        {/* Pricing hero */}
        <div className="border-t pt-3">
          {p.isTyreQuote ? (
            <>
              <p
                className="text-3xl font-heading font-bold text-primary leading-none"
                data-testid={`per-tyre-excl-gst-${quote.quote_id}`}
              >
                ${p.perTyreExclGst.toFixed(2)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                excl. GST per tyre fitted
              </p>
              <p
                className="text-base font-semibold text-foreground/80 mt-1.5 leading-none"
                data-testid={`per-tyre-incl-gst-${quote.quote_id}`}
              >
                ${p.perTyreInclGst.toFixed(2)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                incl. GST per tyre fitted
              </p>
            </>
          ) : (
            <>
              <p className="text-3xl font-heading font-bold text-primary leading-none">
                ${p.totalInclGst.toFixed(2)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">incl. GST total</p>
            </>
          )}
        </div>

        {/* Tyre details */}
        {p.isTyreQuote && (quote.tyre_brand || quote.tyre_model) && (
          <div className="text-xs space-y-0.5 border-t pt-2.5">
            <p className="font-medium text-foreground" data-testid={`tyre-name-${quote.quote_id}`}>
              {quote.tyre_brand} {quote.tyre_model}
            </p>
            <p className="text-muted-foreground">
              {quote.tyre_size && <>{quote.tyre_size} • </>}
              {quote.speed_rating && <>{quote.speed_rating} • </>}
              {quote.stock_availability
                ? quote.stock_availability.replace(/_/g, " ")
                : "in stock"}
            </p>
            {(quote.fitment_estimate || quote.service_estimate) && (
              <p className="text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Earliest fitment date {formatNZDate(quote.fitment_estimate || quote.service_estimate)}
              </p>
            )}
          </div>
        )}
        {!p.isTyreQuote && (quote.fitment_estimate || quote.service_estimate) && (
          <div className="text-xs border-t pt-2.5">
            <p className="text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Earliest fitment date {formatNZDate(quote.fitment_estimate || quote.service_estimate)}
            </p>
          </div>
        )}

        {/* Inclusions */}
        {Array.isArray(quote.inclusions) && quote.inclusions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {quote.inclusions.slice(0, 5).map((inc, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded bg-muted/60 text-foreground/80"
              >
                {inc}
              </span>
            ))}
            {quote.inclusions.length > 5 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded text-muted-foreground">
                +{quote.inclusions.length - 5} more
              </span>
            )}
          </div>
        )}

        {/* Notes — collapsed to 2 lines by default; "Read more" appears only
            when the text actually overflows (measured via ref). Hover tooltip
            (title) still shows the full text for desktop users. */}
        {notes && (
          <div className="text-xs text-muted-foreground italic" data-testid={`quote-notes-${quote.quote_id}`}>
            <p
              ref={notesRef}
              className={notesExpanded ? "" : "line-clamp-2"}
              title={notes}
            >
              “{notes}”
            </p>
            {(notesOverflows || notesExpanded) && (
              <button
                type="button"
                onClick={() => setNotesExpanded((v) => !v)}
                className="mt-0.5 text-[11px] font-medium text-primary hover:underline not-italic"
                data-testid={`toggle-notes-${quote.quote_id}`}
              >
                {notesExpanded ? "Show less" : "Read more"}
              </button>
            )}
          </div>
        )}

        {/* Total breakdown - subtle */}
        {p.isTyreQuote && (
          <div className="text-[11px] text-muted-foreground border-t pt-2 space-y-0.5">
            {p.tyreFitmentInclGst > 0 && (
              <div className="flex justify-between">
                <span>Total fitment</span>
                <span>${p.tyreFitmentInclGst.toFixed(2)}</span>
              </div>
            )}
            {p.alignmentInclGst > 0 && (
              <div className="flex justify-between">
                <span>Tyre alignment</span>
                <span>${p.alignmentInclGst.toFixed(2)}</span>
              </div>
            )}
            {p.rotationInclGst > 0 && (
              <div className="flex justify-between">
                <span>Tyre rotation</span>
                <span>${p.rotationInclGst.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-medium text-foreground pt-0.5 border-t mt-1">
              <span>Total payable</span>
              <span data-testid={`total-payable-${quote.quote_id}`}>
                ${p.totalInclGst.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-1.5 pt-1">
          {accepted ? (
            <Badge variant="outline" className="w-full justify-center py-2 border-green-500/60 text-green-700 dark:text-green-400">
              Quote Accepted
            </Badge>
          ) : interactive ? (
            <Button
              className="w-full bg-primary text-primary-foreground hover:opacity-95"
              onClick={() => onAccept(quote)}
              data-testid={`accept-quote-${quote.quote_id}`}
            >
              Accept Quote ›
            </Button>
          ) : (
            <Button disabled className="w-full" variant="outline">
              {expired ? "Expired" : "Unavailable"}
            </Button>
          )}
          <Link
            to={`/shop/profile/${quote.shop_id}`}
            className="block text-center text-[11px] text-primary hover:underline"
            data-testid={`view-shop-profile-${quote.quote_id}`}
          >
            <ExternalLink className="h-3 w-3 inline mr-0.5" />
            View Shop Profile
          </Link>
        </div>

        <div className="text-center pt-0.5">
          <ExpiryCountdown expiryTime={quote.quote_expiry || request?.expiry_time} />
        </div>
      </div>
    </div>
  );
}

// ---- Main dialog ----
export function CustomerQuotesDialog({
  selectedRequest,
  setSelectedRequest,
  quotes,
  quotesLoading,
  onAcceptQuote,
  autoOpenAi = false,
  onAutoOpenAiConsumed,
}) {
  const [sortBy, setSortBy] = useState("price_asc");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiButtonPulse, setAiButtonPulse] = useState(false);

  // One-shot pulse on the AI button the first time this dialog opens for
  // a given request (and there are at least 2 quotes to compare). Lasts
  // ~5s then stops so it doesn't become noise on repeat opens.
  useEffect(() => {
    if (selectedRequest && (quotes?.length || 0) >= 2) {
      setAiButtonPulse(true);
      const t = setTimeout(() => setAiButtonPulse(false), 5000);
      return () => clearTimeout(t);
    }
    setAiButtonPulse(false);
  }, [selectedRequest?.request_id, quotes?.length]);

  // Auto-open the AI dialog when the parent passed autoOpenAi=true (e.g. the
  // customer clicked "Compare with AI" on the nudge popup).
  useEffect(() => {
    if (autoOpenAi && selectedRequest && (quotes?.length || 0) >= 2) {
      setAiOpen(true);
      if (onAutoOpenAiConsumed) onAutoOpenAiConsumed();
    }
  }, [autoOpenAi, selectedRequest, quotes, onAutoOpenAiConsumed]);

  const sortedQuotes = useMemo(() => {
    const list = (quotes || []).slice();
    const priceOf = (q) => computePricing(q, selectedRequest).totalInclGst;
    switch (sortBy) {
      case "price_desc":
        return list.sort((a, b) => priceOf(b) - priceOf(a));
      case "newest":
        return list.sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
        );
      case "brand_asc":
        return list.sort((a, b) =>
          (a.tyre_brand || "").localeCompare(b.tyre_brand || ""),
        );
      case "price_asc":
      default:
        return list.sort((a, b) => priceOf(a) - priceOf(b));
    }
  }, [quotes, sortBy, selectedRequest]);

  // Cheapest non-expired, non-accepted quote → BEST PRICE badge
  const cheapestId = useMemo(() => {
    const eligible = (quotes || []).filter(
      (q) =>
        !isQuoteExpired(q, selectedRequest) &&
        q.status !== "accepted" &&
        q.status !== "rejected",
    );
    if (!eligible.length) return null;
    const min = eligible.reduce((acc, q) => {
      const price = computePricing(q, selectedRequest).totalInclGst;
      return price < acc.price ? { id: q.quote_id, price } : acc;
    }, { id: null, price: Infinity });
    return min.id;
  }, [quotes, selectedRequest]);

  const requestSummary = useMemo(() => {
    if (!selectedRequest) return "";
    const parts = [];
    parts.push(`${quotes?.length || 0} ${(quotes?.length || 0) === 1 ? "quote" : "quotes"} received`);
    if (selectedRequest.address?.suburb) parts.push(selectedRequest.address.suburb);
    if (selectedRequest.tyre_specs?.width) {
      const t = selectedRequest.tyre_specs;
      const qty = selectedRequest.quantity ? `${selectedRequest.quantity} × ` : "";
      parts.push(`${qty}${t.width}/${t.aspect_ratio || t.profile || "—"}R${t.rim_size || "—"}`);
    }
    return parts.join(" • ");
  }, [selectedRequest, quotes]);

  return (
    <Dialog
      open={!!selectedRequest}
      onOpenChange={() => setSelectedRequest(null)}
    >
      <DialogContent
        className="max-w-[1500px] w-[98vw] h-[95vh] overflow-hidden p-0 flex flex-col"
        data-testid="customer-quotes-dialog"
      >
        <DialogHeader className="px-5 sm:px-6 py-4 border-b shrink-0">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <DialogTitle className="font-heading text-xl sm:text-2xl">
                Quotes for{" "}
                {selectedRequest?.request_number ||
                  `#${selectedRequest?.request_id}`}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {requestSummary}
              </DialogDescription>
            </div>
            {(quotes?.length || 0) > 1 && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => {
                    setAiOpen(true);
                    setAiButtonPulse(false);
                  }}
                  className={`bg-gradient-to-r from-primary to-purple-500 text-white hover:opacity-90 h-8 px-3 text-xs font-semibold relative ${
                    aiButtonPulse ? "ai-pulse-once" : ""
                  }`}
                  data-testid="open-ai-recommendation-btn"
                >
                  <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${aiButtonPulse ? "animate-pulse" : ""}`} />
                  Get AI Recommendation
                  <span
                    className="ml-2 px-1.5 py-0.5 rounded bg-white/25 text-white text-[9px] font-bold tracking-wider"
                    data-testid="ai-beta-badge"
                  >
                    BETA
                  </span>
                </Button>
                <label
                  htmlFor="sort-quotes"
                  className="text-xs text-muted-foreground"
                >
                  Sort by
                </label>
                <select
                  id="sort-quotes"
                  className="px-3 py-1.5 border border-input rounded-md text-sm font-medium bg-background"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  data-testid="sort-quotes-select"
                >
                  <option value="price_asc">Price: low → high</option>
                  <option value="price_desc">Price: high → low</option>
                  <option value="newest">Newest first</option>
                  <option value="brand_asc">Tyre brand A → Z</option>
                </select>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
          {quotesLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !quotes || quotes.length === 0 ? (
            <div className="text-center py-16 px-6">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No quotes received yet</p>
            </div>
          ) : (
            <div
              className="px-5 sm:px-6 py-5 snap-x snap-mandatory md:snap-none flex items-start gap-4 min-w-max"
              data-testid="quote-cards-row"
            >
              {sortedQuotes.map((q) => (
                <QuoteColumn
                  key={q.quote_id}
                  quote={q}
                  request={selectedRequest}
                  isCheapest={q.quote_id === cheapestId}
                  onAccept={onAcceptQuote}
                />
              ))}
            </div>
          )}
        </div>

        {(quotes?.length || 0) > 1 && (
          <p className="text-center text-[11px] text-muted-foreground py-2 border-t shrink-0">
            ⇆ Scroll horizontally to compare all {quotes.length} quotes
          </p>
        )}
      </DialogContent>

      <AIRecommendationDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        request={selectedRequest}
        quotes={quotes}
      />
    </Dialog>
  );
}
